import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Wallet, TrendingUp, Loader2, Plus, Pencil, Save, X, Trash2, PieChart, AlertTriangle, CalendarDays } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AmountInput } from '../components/ui/amount-input';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart as RechartsPie, Pie, Legend } from 'recharts';
import { toast } from 'sonner';
import { Trans, useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ALLOC_COLORS = ['#6366f1', '#f59e0b', '#ef4444', '#14b8a6', '#8b5cf6', '#ec4899', '#f97316', '#06b6d4'];

function Allocations() {
  const { t } = useTranslation('allocations');
  const MONTHS_SHORT = t('common:monthsShort', { returnObjects: true });
  const FULL_MONTHS = t('common:months', { returnObjects: true });

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [displayCurrency, setDisplayCurrency] = useState('UZS');

  // Allocations
  const [allocations, setAllocations] = useState([]);
  const [allocBreakdown, setAllocBreakdown] = useState(null);
  const [allocLoading, setAllocLoading] = useState(false);
  const [showAddAlloc, setShowAddAlloc] = useState(false);
  const [allocForm, setAllocForm] = useState({ name: '', allocation_type: 'percentage', value: '', currency: 'USD', period_year: '', period_month: '' });
  const [allYear, setAllYear] = useState(true);
  const [periodMode, setPeriodMode] = useState('single'); // 'single' | 'range'
  const [periodFrom, setPeriodFrom] = useState({ month: '', year: '' });
  const [periodTo, setPeriodTo] = useState({ month: '', year: '' });
  const [savingAlloc, setSavingAlloc] = useState(false);
  const [editingAlloc, setEditingAlloc] = useState(null);

  const fmtUzs = (v) => `${Math.round(v).toLocaleString('uz-UZ')} UZS`;
  const fmtUsd = (v) => `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtAmount = (uzs, usd) => displayCurrency === 'UZS' ? fmtUzs(uzs) : fmtUsd(usd);

  const fetchAllocations = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/allocations`, { withCredentials: true });
      setAllocations(Array.isArray(res.data) ? res.data : []);
    } catch { /* silent */ }
  }, []);

  const fetchAllocBreakdown = useCallback(async () => {
    setAllocLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/allocations/breakdown?year=${selectedYear}`, { withCredentials: true });
      setAllocBreakdown(res.data);
    } catch { setAllocBreakdown(null); }
    finally { setAllocLoading(false); }
  }, [selectedYear]);

  useEffect(() => { fetchAllocations(); }, [fetchAllocations]);
  useEffect(() => { fetchAllocBreakdown(); }, [fetchAllocBreakdown]);

  const handleSaveAlloc = async () => {
    const name = allocForm.name.trim();
    const value = parseFloat(allocForm.value);
    if (!name || !value || value <= 0) {
      toast.error(t('toasts.nameAndValueRequired'));
      return;
    }
    // Range mode (single allocation with date range)
    if (!allYear && periodMode === 'range' && !editingAlloc) {
      if (!periodFrom.month || !periodFrom.year || !periodTo.month || !periodTo.year) {
        toast.error(t('toasts.rangeRequired'));
        return;
      }
      const fromVal = parseInt(periodFrom.year) * 12 + parseInt(periodFrom.month);
      const toVal = parseInt(periodTo.year) * 12 + parseInt(periodTo.month);
      if (fromVal > toVal) {
        toast.error(t('toasts.rangeInvalid'));
        return;
      }
      setSavingAlloc(true);
      try {
        const payload = {
          name, allocation_type: allocForm.allocation_type, value, currency: allocForm.currency,
          period_year: parseInt(periodFrom.year), period_month: parseInt(periodFrom.month),
          period_to_year: parseInt(periodTo.year), period_to_month: parseInt(periodTo.month),
        };
        await axios.post(`${API_URL}/api/allocations`, payload, { withCredentials: true });
        const cnt = toVal - fromVal + 1;
        toast.success(t('toasts.addedForMonths', { count: cnt }));
        setShowAddAlloc(false);
        setEditingAlloc(null);
        setAllocForm({ name: '', allocation_type: 'percentage', value: '', currency: 'USD', period_year: '', period_month: '' });
        setAllYear(true);
        setPeriodMode('single');
        setPeriodFrom({ month: '', year: '' });
        setPeriodTo({ month: '', year: '' });
        fetchAllocations();
        fetchAllocBreakdown();
      } catch (err) {
        toast.error(err.response?.data?.detail || t('common:status.error'));
      } finally {
        setSavingAlloc(false);
      }
      return;
    }
    // Single mode or edit mode
    if (!allYear && periodMode === 'single' && !allocForm.period_year) {
      toast.error(t('toasts.yearOrPermanent'));
      return;
    }
    if (!allYear && periodMode === 'range') {
      if (!periodFrom.month || !periodFrom.year || !periodTo.month || !periodTo.year) {
        toast.error(t('toasts.rangeRequired'));
        return;
      }
      const fromVal = parseInt(periodFrom.year) * 12 + parseInt(periodFrom.month);
      const toVal = parseInt(periodTo.year) * 12 + parseInt(periodTo.month);
      if (fromVal > toVal) {
        toast.error(t('toasts.rangeInvalid'));
        return;
      }
    }
    if (allocForm.allocation_type === 'percentage' && value > remainingPct) {
      toast.error(t('toasts.percentLimit', { pct: remainingPct }));
      return;
    }
    setSavingAlloc(true);
    try {
      let payload;
      if (!allYear && periodMode === 'range') {
        payload = {
          name, allocation_type: allocForm.allocation_type, value, currency: allocForm.currency,
          period_year: parseInt(periodFrom.year), period_month: parseInt(periodFrom.month),
          period_to_year: parseInt(periodTo.year), period_to_month: parseInt(periodTo.month),
        };
      } else {
        payload = {
          name, allocation_type: allocForm.allocation_type, value, currency: allocForm.currency,
          period_year: allYear ? null : (allocForm.period_year ? parseInt(allocForm.period_year) : null),
          period_month: allYear ? null : (allocForm.period_month ? parseInt(allocForm.period_month) : null),
          period_to_year: null, period_to_month: null,
        };
      }
      if (editingAlloc) {
        await axios.put(`${API_URL}/api/allocations/${editingAlloc}`, payload, { withCredentials: true });
        toast.success(t('toasts.updated'));
      } else {
        await axios.post(`${API_URL}/api/allocations`, payload, { withCredentials: true });
        toast.success(t('toasts.added'));
      }
      setShowAddAlloc(false);
      setEditingAlloc(null);
      setAllocForm({ name: '', allocation_type: 'percentage', value: '', currency: 'USD', period_year: '', period_month: '' });
      setAllYear(true);
      setPeriodMode('single');
      fetchAllocations();
      fetchAllocBreakdown();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common:status.error'));
    } finally {
      setSavingAlloc(false);
    }
  };

  const handleToggleMark = async (allocationId, month, currentPaid) => {
    const newPaid = !currentPaid;
    // Optimistic update
    setAllocBreakdown(prev => ({
      ...prev,
      monthly: prev.monthly.map((m, i) =>
        i + 1 === month
          ? { ...m, allocations: m.allocations.map(d => d.allocation_id === allocationId ? { ...d, is_paid: newPaid } : d) }
          : m
      )
    }));
    try {
      await axios.post(`${API_URL}/api/allocations/mark`, {
        allocation_id: allocationId,
        year: selectedYear,
        month,
        is_paid: newPaid,
      }, { withCredentials: true });
    } catch {
      // Revert on error
      setAllocBreakdown(prev => ({
        ...prev,
        monthly: prev.monthly.map((m, i) =>
          i + 1 === month
            ? { ...m, allocations: m.allocations.map(d => d.allocation_id === allocationId ? { ...d, is_paid: currentPaid } : d) }
            : m
        )
      }));
      toast.error(t('toasts.markFailed'));
    }
  };

  const handleDeleteAlloc = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/allocations/${id}`, { withCredentials: true });
      toast.success(t('toasts.deleted'));
      fetchAllocations();
      fetchAllocBreakdown();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common:status.error'));
    }
  };

  const startEditAlloc = (a) => {
    setEditingAlloc(a.id);
    const hasSpecificPeriod = a.period_year != null;
    const hasRange = a.period_to_year != null && a.period_to_month != null;
    setAllYear(!hasSpecificPeriod);
    if (hasRange) {
      setPeriodMode('range');
      setPeriodFrom({ year: String(a.period_year), month: String(a.period_month) });
      setPeriodTo({ year: String(a.period_to_year), month: String(a.period_to_month) });
    } else {
      setPeriodMode('single');
      setPeriodFrom({ month: '', year: '' });
      setPeriodTo({ month: '', year: '' });
    }
    setAllocForm({
      name: a.name, allocation_type: a.allocation_type, value: String(a.value), currency: a.currency || 'USD',
      period_year: a.period_year ? String(a.period_year) : '',
      period_month: a.period_month ? String(a.period_month) : '',
    });
    setShowAddAlloc(true);
  };

  const years = [];
  for (let y = new Date().getFullYear(); y >= 2023; y--) years.push(y);

  const activeAllocs = allocations.filter(a => a.is_active);

  // Check if two allocation specs can apply to the same month
  const periodsOverlap = (a, b) => {
    const range = (alloc) => {
      const py = alloc.year; const pm = alloc.month;
      const pty = alloc.toYear; const ptm = alloc.toMonth;
      if (py == null) return null;
      if (pty != null && ptm != null) return [py * 12 + (pm || 1), pty * 12 + ptm];
      if (pm == null) return [py * 12 + 1, py * 12 + 12];
      return [py * 12 + pm, py * 12 + pm];
    };
    const ra = range(a); const rb = range(b);
    if (ra == null || rb == null) return true;
    return ra[0] <= rb[1] && rb[0] <= ra[1];
  };

  // Compute used percentage considering period overlap with current form
  const formYear = allocForm.period_year ? parseInt(allocForm.period_year) : null;
  const formMonth = allocForm.period_month ? parseInt(allocForm.period_month) : null;
  const effectiveYear = allYear ? null : formYear;
  const effectiveMonth = allYear ? null : formMonth;
  // For range mode, compute toYear/toMonth from periodFrom/periodTo
  const effectiveToYear = (!allYear && periodMode === 'range' && periodTo.year) ? parseInt(periodTo.year) : null;
  const effectiveToMonth = (!allYear && periodMode === 'range' && periodTo.month) ? parseInt(periodTo.month) : null;
  // Override from fields in range mode
  const formSpecYear = (!allYear && periodMode === 'range' && periodFrom.year) ? parseInt(periodFrom.year) : effectiveYear;
  const formSpecMonth = (!allYear && periodMode === 'range' && periodFrom.month) ? parseInt(periodFrom.month) : effectiveMonth;

  const usedPct = activeAllocs
    .filter(a => a.allocation_type === 'percentage')
    .reduce((sum, a) => {
      if (editingAlloc && a.id === editingAlloc) return sum;
      const formSpec = { year: formSpecYear, month: formSpecMonth, toYear: effectiveToYear, toMonth: effectiveToMonth };
      const allocSpec = { year: a.period_year ?? null, month: a.period_month ?? null, toYear: a.period_to_year ?? null, toMonth: a.period_to_month ?? null };
      if (!periodsOverlap(formSpec, allocSpec)) return sum;
      return sum + a.value;
    }, 0);
  const remainingPct = periodMode === 'range'
    ? 100
    : Math.max(0, 100 - usedPct);
  const totalAllocPct = activeAllocs
    .filter(a => a.allocation_type === 'percentage')
    .reduce((sum, a) => sum + a.value, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Wallet className="h-7 w-7 text-indigo-600" />
            {t('header.title')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('header.subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={displayCurrency} onValueChange={setDisplayCurrency}>
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="UZS">UZS</SelectItem>
              <SelectItem value="USD">USD</SelectItem>
            </SelectContent>
          </Select>
          <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => <SelectItem key={y} value={String(y)}>{t('yearLabel', { year: y })}</SelectItem>)}
            </SelectContent>
          </Select>
          {!showAddAlloc && (
            <Button size="sm" onClick={() => { setShowAddAlloc(true); setEditingAlloc(null); setAllYear(true); setPeriodMode('single'); setPeriodFrom({ month: '', year: '' }); setPeriodTo({ month: '', year: '' }); setAllocForm({ name: '', allocation_type: 'percentage', value: '', currency: 'USD', period_year: '', period_month: '' }); }}>
              <Plus className="h-4 w-4 mr-1" /> {t('header.newAllocation')}
            </Button>
          )}
        </div>
      </div>

      {/* Allocation percentage progress bar */}
      {activeAllocs.some(a => a.allocation_type === 'percentage') && (
        <Card>
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-slate-600 dark:text-slate-400">{t('progress.title')}</span>
              <span className={`font-semibold ${totalAllocPct >= 100 ? 'text-emerald-600' : totalAllocPct > 80 ? 'text-amber-600' : 'text-slate-700 dark:text-slate-300'}`}>
                {totalAllocPct}% / 100%
              </span>
            </div>
            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3 overflow-hidden">
              {(() => {
                let offset = 0;
                return activeAllocs.filter(a => a.allocation_type === 'percentage').map((a, idx) => {
                  const width = Math.min(a.value, 100 - offset);
                  const left = offset;
                  offset += width;
                  return (
                    <div
                      key={a.id}
                      className="h-3 float-left transition-all"
                      style={{ width: `${width}%`, backgroundColor: ALLOC_COLORS[idx % ALLOC_COLORS.length] }}
                      title={`${a.name}: ${a.value}%`}
                    />
                  );
                });
              })()}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
              {activeAllocs.filter(a => a.allocation_type === 'percentage').map((a, idx) => (
                <div key={a.id} className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ALLOC_COLORS[idx % ALLOC_COLORS.length] }} />
                  {a.name}: {a.value}%
                </div>
              ))}
              {100 - totalAllocPct > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-slate-400">
                  <div className="w-2.5 h-2.5 rounded-full bg-slate-200 dark:bg-slate-600" />
                  {t('progress.free', { pct: 100 - totalAllocPct })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 30% recommendation warning */}
      {totalAllocPct > 30 && (
        <Alert className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800 dark:text-amber-300 text-sm">
            <Trans t={t} i18nKey="advice" values={{ pct: totalAllocPct }} components={{ label: <span className="font-medium" />, strong: <strong /> }} />
          </AlertDescription>
        </Alert>
      )}

      {/* Add/Edit allocation form */}
      {showAddAlloc && (
        <Card className="border-indigo-200 dark:border-indigo-800">
          <CardContent className="pt-5">
            <div className="text-sm font-medium text-indigo-700 dark:text-indigo-300 mb-3">
              {editingAlloc ? t('form.editTitle') : t('form.addTitle')}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="lg:col-span-2">
                <Label className="text-xs">{t('form.name')}</Label>
                <Input
                  placeholder={t('form.namePlaceholder')}
                  value={allocForm.name}
                  onChange={(e) => setAllocForm(f => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">{t('form.type')}</Label>
                <Select value={allocForm.allocation_type} onValueChange={(v) => setAllocForm(f => ({ ...f, allocation_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">{t('form.typePercentage')}</SelectItem>
                    <SelectItem value="fixed">{t('form.typeFixed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{allocForm.allocation_type === 'percentage' ? t('form.percentLabel', { pct: remainingPct }) : t('common:fields.amount')}</Label>
                <AmountInput
                  placeholder={allocForm.allocation_type === 'percentage' ? t('form.maxPlaceholder', { pct: remainingPct }) : '500'}
                  value={allocForm.value}
                  onChange={(val) => setAllocForm(f => ({ ...f, value: val }))}
                />
              </div>
              {allocForm.allocation_type === 'fixed' && (
                <div>
                  <Label className="text-xs">{t('common:fields.currency')}</Label>
                  <Select value={allocForm.currency} onValueChange={(v) => setAllocForm(f => ({ ...f, currency: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="UZS">UZS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Period selector */}
            <div className="col-span-full border border-slate-200 dark:border-slate-700 rounded-lg p-3 space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="all-year"
                  checked={allYear}
                  onCheckedChange={(checked) => {
                    setAllYear(checked);
                    if (checked) {
                      setAllocForm(f => ({ ...f, period_year: '', period_month: '' }));
                      setPeriodMode('single');
                    }
                  }}
                />
                <Label htmlFor="all-year" className="text-sm cursor-pointer">
                  {t('form.permanent')}
                </Label>
              </div>
              {!allYear && (
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-slate-500">{t('form.mode')}</Label>
                  <button type="button" onClick={() => setPeriodMode('single')}
                    className={`text-xs px-2 py-1 rounded ${periodMode === 'single' ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 font-medium' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}>
                    {t('form.modeSingle')}
                  </button>
                  <button type="button" onClick={() => setPeriodMode('range')}
                    className={`text-xs px-2 py-1 rounded ${periodMode === 'range' ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 font-medium' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}>
                    {t('form.modeRange')}
                  </button>
                </div>
              )}
              {!allYear && periodMode === 'single' && (
                <div className="flex gap-3 items-end">
                  <div>
                    <Label className="text-xs text-slate-500">{t('form.yearRequired')}</Label>
                    <Select value={allocForm.period_year} onValueChange={(v) => setAllocForm(f => ({ ...f, period_year: v }))}>
                      <SelectTrigger className="h-8 text-xs w-[90px]"><SelectValue placeholder={t('common:fields.year')} /></SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026, 2027].map(y => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">{t('form.monthOptional')}</Label>
                    <Select value={allocForm.period_month || 'all'} onValueChange={(v) => setAllocForm(f => ({ ...f, period_month: v === 'all' ? '' : v }))}>
                      <SelectTrigger className="h-8 text-xs w-[110px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('form.wholeYear')}</SelectItem>
                        {FULL_MONTHS.map((m, idx) => (
                          <SelectItem key={idx} value={String(idx + 1)}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-400 pb-1">
                    {allocForm.period_year
                      ? allocForm.period_month
                        ? t('form.onlyMonth', { month: FULL_MONTHS[parseInt(allocForm.period_month) - 1], year: allocForm.period_year })
                        : t('form.duringYear', { year: allocForm.period_year })
                      : t('form.chooseYear')}
                  </p>
                </div>
              )}
              {!allYear && periodMode === 'range' && (
                <div className="flex flex-wrap gap-3 items-end">
                  <div>
                    <Label className="text-xs text-slate-500">{t('form.fromYear')}</Label>
                    <Select value={periodFrom.year} onValueChange={(v) => setPeriodFrom(f => ({ ...f, year: v }))}>
                      <SelectTrigger className="h-8 text-xs w-[90px]"><SelectValue placeholder={t('common:fields.year')} /></SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026, 2027].map(y => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">{t('form.fromMonth')}</Label>
                    <Select value={periodFrom.month} onValueChange={(v) => setPeriodFrom(f => ({ ...f, month: v }))}>
                      <SelectTrigger className="h-8 text-xs w-[110px]"><SelectValue placeholder={t('common:fields.month')} /></SelectTrigger>
                      <SelectContent>
                        {FULL_MONTHS.map((m, idx) => (
                          <SelectItem key={idx} value={String(idx + 1)}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <span className="text-slate-400 pb-1">→</span>
                  <div>
                    <Label className="text-xs text-slate-500">{t('form.toYear')}</Label>
                    <Select value={periodTo.year} onValueChange={(v) => setPeriodTo(f => ({ ...f, year: v }))}>
                      <SelectTrigger className="h-8 text-xs w-[90px]"><SelectValue placeholder={t('common:fields.year')} /></SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026, 2027].map(y => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">{t('form.toMonth')}</Label>
                    <Select value={periodTo.month} onValueChange={(v) => setPeriodTo(f => ({ ...f, month: v }))}>
                      <SelectTrigger className="h-8 text-xs w-[110px]"><SelectValue placeholder={t('common:fields.month')} /></SelectTrigger>
                      <SelectContent>
                        {FULL_MONTHS.map((m, idx) => (
                          <SelectItem key={idx} value={String(idx + 1)}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {periodFrom.month && periodFrom.year && periodTo.month && periodTo.year && (() => {
                    const cnt = (parseInt(periodTo.year) * 12 + parseInt(periodTo.month)) - (parseInt(periodFrom.year) * 12 + parseInt(periodFrom.month)) + 1;
                    return cnt > 0 ? (
                      <p className="text-xs text-blue-600 dark:text-blue-400 pb-1 font-medium">
                        {t('form.rangeCount', { count: cnt })}
                      </p>
                    ) : (
                      <p className="text-xs text-red-500 pb-1">{t('form.invalidRange')}</p>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-3 col-span-full">
              <Button size="sm" onClick={handleSaveAlloc} disabled={savingAlloc}>
                {savingAlloc ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                {!allYear && periodMode === 'range'
                  ? (() => {
                      if (periodFrom.month && periodFrom.year && periodTo.month && periodTo.year) {
                        const cnt = (parseInt(periodTo.year) * 12 + parseInt(periodTo.month)) - (parseInt(periodFrom.year) * 12 + parseInt(periodFrom.month)) + 1;
                        return cnt > 0 ? t('form.saveWithCount', { count: cnt }) : t('common:actions.save');
                      }
                      return t('common:actions.save');
                    })()
                  : t('common:actions.save')
                }
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowAddAlloc(false); setEditingAlloc(null); }}>
                <X className="h-4 w-4 mr-1" /> {t('common:actions.cancel')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active allocations list */}
      {activeAllocs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('list.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {activeAllocs.map((a, idx) => (
                <div key={a.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: ALLOC_COLORS[idx % ALLOC_COLORS.length] }} />
                    <div>
                      <div className="font-medium text-sm text-slate-800 dark:text-slate-200">{a.name}</div>
                      <div className="text-xs text-slate-500">
                        {a.allocation_type === 'percentage'
                          ? <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">{a.value}%</span>
                          : <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">{a.currency === 'UZS' ? fmtUzs(a.value) : fmtUsd(a.value)}</span>}
                        {a.period_year ? (
                          <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                            <CalendarDays className="h-3 w-3 mr-0.5" />
                            {a.period_to_year && a.period_to_month
                              ? `${MONTHS_SHORT[(a.period_month || 1) - 1]} ${a.period_year} — ${MONTHS_SHORT[a.period_to_month - 1]} ${a.period_to_year}`
                              : a.period_month ? `${MONTHS_SHORT[a.period_month - 1]} ${a.period_year}` : t('yearLabel', { year: a.period_year })}
                          </span>
                        ) : (
                          <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400">{t('list.permanent')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => startEditAlloc(a)}>
                      <Pencil className="h-3.5 w-3.5 text-slate-500" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDeleteAlloc(a.id)}>
                      <Trash2 className="h-3.5 w-3.5 text-red-400" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Breakdown stats */}
      {allocLoading ? (
        <Card>
          <CardContent className="py-12 flex justify-center items-center gap-2 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" /> {t('breakdown.calculating')}
          </CardContent>
        </Card>
      ) : allocBreakdown && allocBreakdown.summary?.length > 0 ? (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {allocBreakdown.summary.map((s, idx) => (
              <Card key={s.id}>
                <CardContent className="pt-5">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ALLOC_COLORS[idx % ALLOC_COLORS.length] }} />
                    <div className="text-sm text-slate-500 dark:text-slate-400 truncate">{s.name}</div>
                  </div>
                  <div className="text-xl font-bold text-slate-900 dark:text-slate-100">
                    {fmtAmount(s.total_uzs, s.total_usd)}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">{t('breakdown.yearTotal', { year: selectedYear })}</div>
                </CardContent>
              </Card>
            ))}
            <Card className="bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800">
              <CardContent className="pt-5">
                <div className="text-sm text-indigo-600 dark:text-indigo-400 mb-1">{t('breakdown.grandTotal')}</div>
                <div className="text-xl font-bold text-indigo-700 dark:text-indigo-300">
                  {fmtAmount(allocBreakdown.grand_total_uzs, allocBreakdown.grand_total_usd)}
                </div>
                <div className="text-xs text-indigo-400 mt-1">{t('yearLabel', { year: selectedYear })}</div>
              </CardContent>
            </Card>
          </div>

          {/* Pie chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <PieChart className="h-5 w-5" />
                {t('breakdown.pieTitle', { year: selectedYear })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={allocBreakdown.summary.map((s) => ({
                        name: s.name,
                        value: displayCurrency === 'UZS' ? Math.round(s.total_uzs) : s.total_usd,
                      }))}
                      cx="50%" cy="50%"
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {allocBreakdown.summary.map((_, idx) => (
                        <Cell key={idx} fill={ALLOC_COLORS[idx % ALLOC_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => displayCurrency === 'UZS' ? fmtUzs(v) : fmtUsd(v)} />
                    <Legend />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Monthly allocation table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('breakdown.tableTitle')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 dark:bg-slate-700">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 dark:text-slate-400">{t('common:fields.month')}</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600 dark:text-slate-400">{t('table.income')}</th>
                      {allocBreakdown.summary.map((s, idx) => (
                        <th key={s.id} className="px-3 py-2 text-right text-xs font-semibold" style={{ color: ALLOC_COLORS[idx % ALLOC_COLORS.length] }}>{s.name}</th>
                      ))}
                      <th className="px-3 py-2 text-right text-xs font-semibold text-indigo-600 dark:text-indigo-400">{t('table.totalAlloc')}</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-emerald-600 dark:text-emerald-400">{t('table.remaining')}</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-slate-500 dark:text-slate-400">{t('table.paid')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allocBreakdown.monthly.map((m, i) => (
                      <tr key={i} className={`border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/30 ${m.allocations.length > 0 && m.allocations.every(d => d.is_paid) ? 'bg-emerald-50/40 dark:bg-emerald-900/10' : ''}`}>
                        <td className="px-3 py-2 font-medium text-slate-700 dark:text-slate-300">{MONTHS_SHORT[i]}</td>
                        <td className="px-3 py-2 text-right text-slate-600 dark:text-slate-400">{fmtAmount(m.earnings_uzs, m.earnings_usd)}</td>
                        {/* One cell per header column: an allocation outside its period is absent from m.allocations */}
                        {allocBreakdown.summary.map((s) => {
                          const d = m.allocations.find(x => x.allocation_id === s.id);
                          return (
                            <td key={s.id} className="px-3 py-2 text-right text-slate-600 dark:text-slate-400">
                              {d ? (
                                <span className={d.is_paid ? 'line-through opacity-50' : ''}>{fmtAmount(d.amount_uzs, d.amount_usd)}</span>
                              ) : (
                                <span className="text-slate-300 dark:text-slate-600">—</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 text-right font-medium text-indigo-600 dark:text-indigo-400">{fmtAmount(m.total_alloc_uzs, m.total_alloc_usd)}</td>
                        <td className="px-3 py-2 text-right font-medium text-emerald-600 dark:text-emerald-400">{fmtAmount(m.remaining_uzs, m.remaining_usd)}</td>
                        <td className="px-3 py-2 text-center">
                          {m.allocations.length > 0 ? (
                            <div className="flex items-center justify-center gap-1">
                              {m.allocations.map((d) => (
                                <button
                                  key={d.allocation_id}
                                  onClick={() => handleToggleMark(d.allocation_id, m.month, d.is_paid)}
                                  title={`${d.name} — ${d.is_paid ? t('table.paidMark') : t('table.unpaid')}`}
                                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                    d.is_paid
                                      ? 'bg-emerald-500 border-emerald-500 text-white'
                                      : 'border-slate-300 dark:border-slate-600 hover:border-emerald-400'
                                  }`}
                                >
                                  {d.is_paid && <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/></svg>}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-indigo-50 dark:bg-indigo-900/20 font-semibold">
                    <tr>
                      <td className="px-3 py-3 text-indigo-700 dark:text-indigo-400">{t('common:fields.total')}</td>
                      <td className="px-3 py-3 text-right text-indigo-700 dark:text-indigo-400">
                        {fmtAmount(
                          allocBreakdown.monthly.reduce((s, m) => s + m.earnings_uzs, 0),
                          allocBreakdown.monthly.reduce((s, m) => s + m.earnings_usd, 0)
                        )}
                      </td>
                      {allocBreakdown.summary.map((s) => (
                        <td key={s.id} className="px-3 py-3 text-right text-indigo-700 dark:text-indigo-400">{fmtAmount(s.total_uzs, s.total_usd)}</td>
                      ))}
                      <td className="px-3 py-3 text-right text-indigo-700 dark:text-indigo-400">{fmtAmount(allocBreakdown.grand_total_uzs, allocBreakdown.grand_total_usd)}</td>
                      <td className="px-3 py-3 text-right text-emerald-600 dark:text-emerald-400">
                        {fmtAmount(
                          allocBreakdown.monthly.reduce((s, m) => s + m.remaining_uzs, 0),
                          allocBreakdown.monthly.reduce((s, m) => s + m.remaining_usd, 0)
                        )}
                      </td>
                      <td className="px-3 py-3 text-center text-xs text-slate-400">
                        {t('table.monthsPaid', {
                          paid: allocBreakdown.monthly.filter(m => m.allocations.length > 0 && m.allocations.every(d => d.is_paid)).length,
                          total: allocBreakdown.monthly.filter(m => m.allocations.length > 0).length,
                        })}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Stacked bar chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingUp className="h-5 w-5" />
                {t('breakdown.barTitle', { year: selectedYear })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={allocBreakdown.monthly.map((m, i) => {
                    const row = { name: MONTHS_SHORT[i] };
                    m.allocations.forEach((d) => {
                      row[d.name] = displayCurrency === 'UZS' ? Math.round(d.amount_uzs) : d.amount_usd;
                    });
                    row[t('table.remaining')] = displayCurrency === 'UZS' ? Math.round(m.remaining_uzs) : m.remaining_usd;
                    return row;
                  })}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => displayCurrency === 'UZS' ? `${(v / 1000000).toFixed(0)}M` : `$${v}`} />
                    <Tooltip formatter={(v) => displayCurrency === 'UZS' ? fmtUzs(v) : fmtUsd(v)} />
                    <Legend />
                    {allocBreakdown.summary.map((s, idx) => (
                      <Bar key={s.id} dataKey={s.name} stackId="a" fill={ALLOC_COLORS[idx % ALLOC_COLORS.length]} />
                    ))}
                    <Bar dataKey={t('table.remaining')} stackId="a" fill="#10b981" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </>
      ) : !allocLoading && activeAllocs.length === 0 && !showAddAlloc ? (
        <Card>
          <CardContent className="py-10 text-center">
            <Wallet className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400 mb-2">{t('empty.title')}</p>
            <p className="text-sm text-slate-400 mb-4">{t('empty.hint')}</p>
            <Button size="sm" onClick={() => setShowAddAlloc(true)}>
              <Plus className="h-4 w-4 mr-1" /> {t('empty.cta')}
            </Button>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

export default Allocations;
